import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import { type PartnerEventDetail } from '@/api/routes/partners/partners.events.api';
import { IconCalendar } from '@/components/icon/tabler/IconCalendar';
import { IconExternalLink } from '@/components/icon/tabler/IconExternalLink';
import { IconMapPin } from '@/components/icon/tabler/IconMapPin';
import { IconRepeat } from '@/components/icon/tabler/IconRepeat';
import {
  eventAddressLabel,
  eventRecurrenceShortLabel,
  formatEventWhen,
} from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  event: PartnerEventDetail;
}

/** Main column of the detail: banner, information, when and where, as the user will see them. */
const EventDetailInfo = ({ event }: Props) => {
  const t = useTranslate('domain.events.components.EventDetailInfo');
  const recurrence = eventRecurrenceShortLabel(event);
  const address = event.addresses[0];

  return (
    <Paper variant="outlined" sx={sx.paper}>
      {event.bannerUrl ? (
        <Box component="img" src={event.bannerUrl} alt={t('bannerAlt')} sx={sx.banner} />
      ) : null}
      <Stack gap={2.5} sx={{ p: { xs: 2.5, sm: 3 } }}>
        {event.eventType ? (
          <Typography variant="overline" color="text.secondary">
            {event.eventType}
          </Typography>
        ) : null}
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {event.description || t('noDescription')}
        </Typography>
        <Row icon={<IconCalendar fontSize="small" />} label={t('when')}>
          {formatEventWhen(event.startDate, event.startTime)}
          {event.recurrenceUntil
            ? ` · ${t('until', { date: formatEventWhen(event.recurrenceUntil) })}`
            : ''}
        </Row>
        {recurrence ? (
          <Row icon={<IconRepeat fontSize="small" />} label={t('repeats')}>
            {recurrence}
          </Row>
        ) : null}
        <Row icon={<IconMapPin fontSize="small" />} label={t('where')}>
          {eventAddressLabel(address) ?? '—'}
          {address?.complement ? ` (${address.complement})` : ''}
        </Row>
        {event.externalUrl ? (
          <Row icon={<IconExternalLink fontSize="small" />} label={t('link')}>
            <Link href={event.externalUrl} target="_blank" rel="noopener noreferrer">
              {event.externalUrl}
            </Link>
          </Row>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default EventDetailInfo;

const Row = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start">
    <Box sx={{ color: 'text.secondary', pt: '2px' }}>{icon}</Box>
    <Stack minWidth={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Stack>
  </Stack>
);

const sx = {
  paper: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
  banner: { width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' },
};
